import Phaser from "phaser";
import { findLevelByPassword } from "../data/levelData";

export class PasswordScene extends Phaser.Scene {
  private passwordChars: string[] = ["", "", "", ""];
  private passwordTexts: Phaser.GameObjects.Text[] = [];
  private passwordBoxes: Phaser.GameObjects.Rectangle[] = [];
  private errorText: Phaser.GameObjects.Text | null = null;

  constructor() {
    super({ key: "PasswordScene" });
  }

  preload(): void {
    // Load background image
    this.load.image("password-bg", "images/home2.png");
  }

  create(): void {
    // Reset password
    this.passwordChars = ["", "", "", ""];
    this.passwordTexts = [];
    this.passwordBoxes = [];

    const width = 1024;
    const height = 768;

    // Resize canvas to 1024x768
    this.scale.resize(width, height);

    // Display background centered
    this.add.image(512, 384, "password-bg").setOrigin(0.5);

    // Title
    const titleText = this.add.text(width / 2, 200, "ENTER PASSWORD", {
      fontSize: "48px",
      color: "#ffffff",
      fontFamily: "Arial",
      fontStyle: "bold",
      stroke: "#000000",
      strokeThickness: 6,
    });
    titleText.setOrigin(0.5);

    // Password boxes (brick style)
    const boxSize = 60;
    const boxSpacing = 15;
    const totalWidth = 4 * boxSize + 3 * boxSpacing;
    const startX = width / 2 - totalWidth / 2 + boxSize / 2;
    const passwordY = 350;

    for (let i = 0; i < 4; i++) {
      const x = startX + i * (boxSize + boxSpacing);

      // Box background (brick colored)
      const box = this.add.rectangle(x, passwordY, boxSize, boxSize, 0xc84c0c);
      box.setStrokeStyle(4, 0x000000);
      this.passwordBoxes.push(box);

      // Letter text
      const letterText = this.add.text(x, passwordY, "", {
        fontSize: "36px",
        color: "#ffffff",
        fontFamily: "Arial",
        fontStyle: "bold",
        stroke: "#000000",
        strokeThickness: 4,
      });
      letterText.setOrigin(0.5);
      this.passwordTexts.push(letterText);
    }

    // Buttons row
    const buttonY = 480;

    // GO button (golden ? block style)
    const goButton = this.add.rectangle(
      width / 2 - 100,
      buttonY,
      140,
      60,
      0xf8b800
    );
    goButton.setStrokeStyle(4, 0x000000);
    goButton.setInteractive({ useHandCursor: true });

    const goText = this.add.text(width / 2 - 100, buttonY, "GO", {
      fontSize: "32px",
      color: "#ffffff",
      fontFamily: "Arial",
      fontStyle: "bold",
      stroke: "#000000",
      strokeThickness: 4,
    });
    goText.setOrigin(0.5);

    goButton.on("pointerover", () => {
      goButton.setFillStyle(0xf8d878);
    });

    goButton.on("pointerout", () => {
      goButton.setFillStyle(0xf8b800);
    });

    goButton.on("pointerdown", () => {
      this.tryPassword();
    });

    // BACK button (green pipe style)
    const backButton = this.add.rectangle(
      width / 2 + 100,
      buttonY,
      140,
      60,
      0x00a800
    );
    backButton.setStrokeStyle(4, 0x000000);
    backButton.setInteractive({ useHandCursor: true });

    const backText = this.add.text(width / 2 + 100, buttonY, "BACK", {
      fontSize: "32px",
      color: "#ffffff",
      fontFamily: "Arial",
      fontStyle: "bold",
      stroke: "#000000",
      strokeThickness: 4,
    });
    backText.setOrigin(0.5);

    backButton.on("pointerover", () => {
      backButton.setFillStyle(0x00d800);
    });

    backButton.on("pointerout", () => {
      backButton.setFillStyle(0x00a800);
    });

    backButton.on("pointerdown", () => {
      this.goBack();
    });

    // Error text (hidden initially)
    this.errorText = this.add.text(width / 2, 560, "", {
      fontSize: "18px",
      color: "#f83800",
      fontFamily: "Arial",
      fontStyle: "bold",
      stroke: "#000000",
      strokeThickness: 3,
    });
    this.errorText.setOrigin(0.5);

    // Keyboard input
    this.input.keyboard?.on("keydown", (event: KeyboardEvent) => {
      this.handleKeyPress(event);
    });
  }

  private handleKeyPress(event: KeyboardEvent): void {
    // Clear error on any key press
    if (this.errorText) {
      this.errorText.setText("");
    }

    // Enter to submit password
    if (event.code === "Enter") {
      this.tryPassword();
      return;
    }

    // Escape or Backspace on empty to go back
    if (event.code === "Escape") {
      this.goBack();
      return;
    }

    // Backspace to delete
    if (event.code === "Backspace") {
      for (let i = 3; i >= 0; i--) {
        if (this.passwordChars[i] !== "") {
          this.passwordChars[i] = "";
          this.updatePasswordDisplay();
          break;
        }
      }
      return;
    }

    let letter: string | null = null;
    // Letter keys
    if (event.code.startsWith("Key")) {
      letter = event.code.substring(3).toUpperCase();
    } else if (event.code.startsWith("Digit")) {
      const digit = event.code.substring(5);
      letter = digit;
    }

    if (letter) {
      // Add letter to first empty slot
      for (let i = 0; i < 4; i++) {
        if (this.passwordChars[i] === "") {
          this.passwordChars[i] = letter;
          this.updatePasswordDisplay();
          break;
        }
      }
    }
  }

  private updatePasswordDisplay(): void {
    for (let i = 0; i < 4; i++) {
      this.passwordTexts[i].setText(this.passwordChars[i]);

      // Highlight filled boxes (golden when filled)
      if (this.passwordChars[i] !== "") {
        this.passwordBoxes[i].setFillStyle(0xf8b800);
      } else {
        this.passwordBoxes[i].setFillStyle(0xc84c0c);
      }
    }
  }

  private tryPassword(): void {
    const password = this.passwordChars.join("");

    if (password.length < 4) {
      if (this.errorText) {
        this.errorText.setText("Enter 4 letters");
      }
      return;
    }

    const levelIndex = findLevelByPassword(password);

    if (levelIndex >= 0) {
      // Store the starting level index
      (window as any).__startLevelIndex = levelIndex;
      this.scene.start("GameScene");
    } else {
      if (this.errorText) {
        this.errorText.setText("Invalid password");
      }
      // Flash the boxes red
      this.passwordBoxes.forEach((box) => {
        box.setStrokeStyle(4, 0xff4444);
      });
      this.time.delayedCall(500, () => {
        this.passwordBoxes.forEach((box, i) => {
          box.setStrokeStyle(4, 0x000000);
        });
        this.updatePasswordDisplay();
      });
    }
  }

  private goBack(): void {
    this.scene.start("HomeScene");
  }
}
